/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { FixedSizeList } from "react-window";
import React, { useCallback, useRef } from "react";
import { Box, ListItemButton } from "@mui/material";
import { Loader } from "@jda/lui-common-icon-library-mui5";

import useStyles from "./styles";
import { SearchListProps, IVirtualScrollConfig, IVirtualScrollProps } from "../../models/SearchList/SearchList";

const virtualScrollConfig: IVirtualScrollConfig = {
  height: 752,
  width: "100%",
  rowHeight: 48,
};

export const SearchList: React.FC<SearchListProps> = (props) => {
  const classes = useStyles();
  const observerPanel: any = useRef();

  const { listStyle, key, onPageChange, currentPage, totalCount } = props;
  const lastElementRef = useCallback(node => {
    // pagination for panel
    if (observerPanel.current) observerPanel.current.disconnect();

    observerPanel.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        if (totalCount && (totalCount > props.data?.length)) {
          onPageChange && !props.isLoading && onPageChange((currentPage || 1) + 1);
        } else {
        }
      }
    });
    if (node) observerPanel.current.observe(node);
  }, [totalCount, props.data?.length]);

  const renderListRow = (virtalScrollProps: IVirtualScrollProps) => {
    const { index, style } = virtalScrollProps;
    let listItem = props.data[index];
    return (
      <ListItemButton
        key={index}
        style={style}
        ref={lastElementRef}
        id={key || "listItem"}
        divider={props.addDivider}
        disableGutters={props.disableGutters}
        selected={props.selectedItem?.id === listItem?.id}
        alignItems={!props.centeredItem ? "flex-start" : "center"}
        onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
          props.onClick(event, listItem)
        }
      >
        {props.renderItem(listItem, index)}
      </ListItemButton>
    );
  };
  return (
    <Box className={classes.root}>
      <FixedSizeList
        className={listStyle}
        itemCount={props.data?.length}
        width={virtualScrollConfig.width}
        height={props.height || virtualScrollConfig.height}
        itemSize={props.minHeight || virtualScrollConfig.rowHeight}
      >
        {(virtalScrollProps: IVirtualScrollProps) => {
          return renderListRow(virtalScrollProps);
        }}
      </FixedSizeList>
      {props.isLoading && (
        <Box className={classes.loadingContainer}>
          <Loader
            color="primary"
            className={classes.loader}
            data-testid="search-list-loader"
          />
        </Box>
      )}
    </Box>
  );
};

export default SearchList;